import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { get, set } from 'lodash-es';
import { BehaviorSubject, of } from 'rxjs';
import { debounceTime, filter, skip, switchMap, tap } from 'rxjs/operators';

import { UserLayoutApiService } from '../api/user-layout/user-layout-api.service';
import { NotificationsService } from '../notifications.service';
import { SessionService } from '../session.service';
import { UserLayout } from './models';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UserLayoutManagerService {
  private readonly layout$ = new BehaviorSubject<UserLayout | null>(null);

  readonly layoutUpdates$ = this.layout$.asObservable();

  private readonly userLayoutApiService = inject(UserLayoutApiService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly sessionService = inject(SessionService);

  constructor() {
    if (environment.featureFlags.userLayout) {
      this.layout$
        .pipe(
          skip(1),
          debounceTime(500),
          filter((layout) => layout != null),
          switchMap((layout) =>
            this.userLayoutApiService.saveUserLayout(
              layout,
              this.sessionService.getUserId(),
            ),
          ),
          takeUntilDestroyed(),
        )
        .subscribe({
          error: () => {
            this.notificationsService.openSnackbar({
              panelClass: 'snackbar-fail',
              data: { message: 'An error occurred while saving user layout.' },
            });
          },
        });
    }
  }

  initializeLayout() {
    if (!environment.featureFlags.userLayout) {
      return of({});
    }

    return this.userLayoutApiService
      .fetchLayout(this.sessionService.getUserId())
      .pipe(tap((layout) => this.layout$.next(layout)));
  }

  updateUserLayout(layoutPath: string, layoutPartial: unknown) {
    const layout = this.layout$.getValue() ?? {};
    this.layout$.next(set({ ...layout }, layoutPath, layoutPartial));
  }

  getUserLayout(layoutPath?: string): unknown {
    if (!layoutPath) {
      return this.layout$.getValue();
    }

    return get(this.layout$.getValue(), layoutPath);
  }
}
